import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Ruler, Layers, Palette } from "lucide-react";
import Layout from "../components/Layout";
import DoorCard from "../components/DoorCard";
import { useLang, localized } from "../lib/i18n";
import { api, FILE_URL } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1628744448840-55bdb2497bd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBmcm9udCUyMGRvb3IlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODI0NzE3MzV8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.pexels.com/photos/38246112/pexels-photo-38246112.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DEFAULT_DOOR = "https://images.unsplash.com/photo-1659720879327-827462ca3942?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsJTIwaW50ZXJpb3IlMjBkb29yfGVufDB8fHx8MTc4NDE0NzE2Mnww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, lang } = useLang();
  const [doors, setDoors] = useState([]);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/doors").then((r) => setDoors(r.data.slice(0, 6))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <section className="w-full bg-neutral-950 overflow-hidden" aria-label="Friend Doors video">
        <video
          src="/media/hero.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="block w-full h-auto max-h-[85vh] object-cover"
          data-testid="hero-video"
        />
      </section>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero">
        {/* Vertical rail on the far left */}
        <div className="hidden md:flex absolute left-6 top-24 bottom-24 flex-col justify-between items-center pointer-events-none z-[3]">
          <span className="fd-label rotate-180" style={{ writingMode: "vertical-rl" }}>Bakı — Est. 2013</span>
          <div className="w-px flex-1 my-4 bg-neutral-200" />
          <span className="fd-label" style={{ writingMode: "vertical-rl" }}>Scroll ↓</span>
        </div>

        <div className="fd-container pt-12 md:pt-20 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-12 md:pl-12">
            <motion.p
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="fd-label mb-6"
            >
              {t("hero.eyebrow")}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-[0.95] text-neutral-900"
              data-testid="hero-title"
            >
              {t("hero.title")}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 text-neutral-500 max-w-md text-base leading-relaxed"
            >
              {t("hero.subtitle")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <Link to="/catalog" className="fd-btn-primary" data-testid="hero-cta-catalog">
                {t("hero.cta_primary")} <ArrowRight size={16} />
              </Link>
              <Link to="/pdfs" className="fd-btn-ghost" data-testid="hero-cta-pdfs">
                {t("hero.cta_secondary")}
              </Link>
            </motion.div>

            {/* Marquee / meta strip below CTAs to occupy left area */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12 md:mt-16 grid grid-cols-3 gap-4 border-t border-neutral-200 pt-6 max-w-2xl"
            >
              {t("home.stats").map((s, i) => (
                <div key={i}>
                  <p className="text-3xl md:text-4xl font-semibold tracking-tight text-[#8b623e] tabular-nums">{s.k}</p>
                  <p className="mt-2 text-xs sm:text-sm font-medium text-neutral-600 leading-relaxed">{s.v}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="fd-container py-16 md:py-24" data-testid="featured-section">
        <div className="flex items-end justify-between mb-10 md:mb-16">
          <div>
            <p className="fd-label mb-4">— 01</p>
            <h2 className="text-3xl md:text-5xl font-light tracking-tight text-neutral-900">{t("home.featured")}</h2>
          </div>
          <Link to="/catalog" className="hidden md:inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-neutral-900 transition-colors" data-testid="featured-view-all">
            {t("home.all_products")} <ArrowUpRight size={16} />
          </Link>
        </div>

        {doors.length === 0 ? (
          <div className="border border-dashed border-neutral-200 py-16 text-center text-sm text-neutral-400" data-testid="featured-empty">
            {t("catalog.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {doors.map((d) => (
              <DoorCard key={d.id} door={d} />
            ))}
          </div>
        )}
      </section>

      {/* ABOUT — asymmetric */}
      <section className="fd-container py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16" data-testid="about-section">
        <div className="md:col-span-5 md:col-start-1 aspect-[4/5] overflow-hidden bg-neutral-100">
          <img src={ABOUT_IMG} alt="workshop" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-6 md:col-start-7 flex flex-col justify-center">
          <p className="fd-label mb-4">— 02 {t("home.about_label")}</p>
          <h2 className="text-3xl md:text-5xl font-light tracking-tight text-neutral-900 leading-tight">{t("home.about_title")}</h2>
          <p className="mt-6 text-neutral-500 leading-relaxed max-w-md">{t("home.about_body")}</p>
          <div className="mt-12 grid grid-cols-3 gap-4 border-t border-neutral-200 pt-8">
            {t("home.stats").map((s, i) => (
              <div key={i}>
                <p className="text-3xl md:text-4xl font-semibold tracking-tight text-[#8b623e] tabular-nums">{s.k}</p>
                <p className="mt-2 text-xs sm:text-sm font-medium text-neutral-600 leading-relaxed">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="fd-container py-16 md:py-24" data-testid="features-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            { icon: Ruler, k: t("door.dimensions"), v: lang === "az" ? "Fərdi ölçülər üzrə istehsal" : lang === "ru" ? "Индивидуальные размеры" : "Custom sizes on request" },
            { icon: Layers, k: t("door.material"), v: lang === "az" ? "Palıd, ceviz, MDF, metal" : lang === "ru" ? "Дуб, орех, МДФ, металл" : "Oak, walnut, MDF, metal" },
            { icon: Palette, k: t("door.colors"), v: lang === "az" ? "12+ rəng variantı" : lang === "ru" ? "12+ вариантов цвета" : "12+ colour options" },
          ].map((f, i) => (
            <div key={i} className="border border-neutral-200 p-8 md:p-10">
              <f.icon size={22} strokeWidth={1.5} className="text-neutral-900" />
              <p className="fd-label mt-6">{f.k}</p>
              <p className="text-neutral-900 text-lg mt-2 leading-snug">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="fd-container py-16 md:py-24">
        <div className="bg-neutral-900 text-white p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end" data-testid="cta-section">
          <div className="md:col-span-8">
            <p className="fd-label !text-neutral-400 mb-4">— 03</p>
            <h2 className="text-3xl md:text-5xl font-light tracking-tight leading-tight">{t("home.cta_title")}</h2>
            <p className="mt-4 text-neutral-400 max-w-md">{t("home.cta_body")}</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <Link to="/catalog" className="inline-flex items-center gap-2 bg-white text-neutral-900 px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-neutral-200 transition-colors" data-testid="cta-catalog">
              {t("hero.cta_primary")} <ArrowRight size={16} />
            </Link>
            <Link to="/pdfs" className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white hover:text-neutral-900 transition-colors" data-testid="cta-pdfs">
              {t("hero.cta_secondary")}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}




